/**
 * MiniProjectSummaryTab — Task #228.
 *
 * Assembled content for the Summary sub-tab of the new MP Management
 * navigation. Skip's design from 2026-05-27:
 *
 *   1. Project Timeline progress bar (elapsed time, colored by the
 *      existing MP schedule classifier)
 *   2. TipTap StatusSection (unchanged — same component used by the
 *      legacy magazine spread)
 *   3. Milestones table (new MiniProjectMilestoneList widget)
 *   4. Mission Status block (per-goal assessment dots; mirror of the
 *      FP MissionStatusCard pattern, MP shape)
 *
 * Presentational: owns no data fetches of its own. ProgressTab (which
 * already loads goals + status) passes them in as props. Mutation
 * handlers (onEditStatus / onNewStatus) come from the parent so the
 * existing modal state remains under ProgressTab's control.
 *
 * Tasks/Resources/Goals from the legacy magazine spread are NOT here —
 * those land in the Schedule, Budget, and Mission sub-tabs respectively
 * (tasks #229, #232, #230). Until those sub-tabs are built, those
 * sections are temporarily not visible on the MP detail page.
 */

import type { MiniProject } from "../../api/miniProjects";
import type { MiniProjectGoalView } from "../../Data/MiniProjectConfig/miniProjectGoalView";
import {
  kpiColorHex,
  type KpiColor,
} from "../../Pages/MiniProjectDetail/computeKpiStatus";
import { getActiveIndicatorPalette } from "../../theme/colorBlindMode";
import type { MiniProjectStatus } from "../../api/miniProjectStatuses";
import StatusSection from "../StatusSection/StatusSection";
import MiniProjectMilestoneList from "./MiniProjectMilestoneList";

interface Props {
  miniProject: MiniProject;
  goals: MiniProjectGoalView[];
  isLead: boolean;
  /** Color from computeMiniProjectKpiStatus().schedule. Drives the
   *  Project Timeline bar's fill color so it stays in sync with the
   *  Schedule KPI dot at the top of the page. */
  scheduleColor: KpiColor;
  /** Matches StatusSection's onEditStatus signature — receives the
   *  selected status row. Parent (ProgressTab) wires this to its
   *  StatusWriter modal state. */
  onEditStatus?: (status: MiniProjectStatus) => void;
  onNewStatus?: () => void;
}

export default function MiniProjectSummaryTab({
  miniProject,
  goals,
  isLead,
  scheduleColor,
  onEditStatus,
  onNewStatus,
}: Props) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ProjectTimelineBar
        startDate={miniProject.startDate ?? null}
        endDate={miniProject.endDate ?? null}
        scheduleColor={scheduleColor}
      />

      {/* Existing StatusSection — same instance the legacy magazine
          spread rendered. No prop changes. */}
      <StatusSection
        miniProjectGuid={miniProject.miniProjectGuid}
        isLead={isLead}
        onEditStatus={onEditStatus}
        onNewStatus={onNewStatus}
      />

      {/* Two-up bottom row: Milestones on the left, Mission Status on
          the right. Matches the FP Summary layout. Collapses to single
          column on narrow viewports via auto-fit. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12,
        }}
      >
        <MiniProjectMilestoneList
          miniProjectGuid={miniProject.miniProjectGuid}
          canEdit={isLead}
        />
        <MissionStatusBlock goals={goals} />
      </div>
    </div>
  );
}

/* ── Project Timeline bar ────────────────────────────────── */

function ProjectTimelineBar({
  startDate,
  endDate,
  scheduleColor,
}: {
  startDate: string | null;
  endDate: string | null;
  scheduleColor: KpiColor;
}) {
  const pct = computeElapsedPercent(startDate, endDate);
  const color = kpiColorHex(scheduleColor);

  return (
    <div
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 0.5,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          marginBlockEnd: 8,
        }}
      >
        Project Timeline
      </div>
      <div
        style={{
          position: "relative",
          blockSize: 12,
          background: "var(--bg-active)",
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            insetBlockStart: 0,
            insetInlineStart: 0,
            inlineSize: `${pct}%`,
            blockSize: "100%",
            background: color,
            borderRadius: 999,
            transition: "inline-size 240ms ease",
          }}
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBlockStart: 6,
          fontSize: 12,
        }}
      >
        <div>
          <span style={{ color: "var(--text-muted)" }}>Start:</span> {fmtDate(startDate)}
        </div>
        <div style={{ color, fontWeight: 500 }}>{pct}%</div>
        <div>
          <span style={{ color: "var(--text-muted)" }}>Finish:</span> {fmtDate(endDate)}
        </div>
      </div>
    </div>
  );
}

/** Compute time-elapsed percent from start → end against today.
 *  Clamped to [0, 100]. Returns 0 when either date is missing. */
function computeElapsedPercent(start: string | null, end: string | null): number {
  if (!start || !end) return 0;
  const startStamp = Date.parse(start);
  const endStamp = Date.parse(end);
  if (!Number.isFinite(startStamp) || !Number.isFinite(endStamp)) return 0;
  if (endStamp <= startStamp) return 0;
  const now = Date.now();
  if (now <= startStamp) return 0;
  if (now >= endStamp) return 100;
  return Math.round(((now - startStamp) / (endStamp - startStamp)) * 100);
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/* ── Mission Status block ────────────────────────────────── */

/** Per-goal assessment list. Color dot + goal name + assessment word.
 *  Each row sourced from MiniProjectGoalView.latestAssessment, which is
 *  derived at the view boundary in miniProjectGoalView.ts.
 *
 *  The fuller mission heatmap lives on the dedicated Mission sub-tab
 *  (task #230). This block is the at-a-glance overview for Summary. */
function MissionStatusBlock({ goals }: { goals: MiniProjectGoalView[] }) {
  const visible = goals.filter((g) => g.includedInRollup !== false);

  return (
    <div
      style={{
        background: "var(--bg-surface)",
        border: "1px solid var(--border-default)",
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: 0.5,
          color: "var(--text-muted)",
          textTransform: "uppercase",
          marginBlockEnd: 10,
        }}
      >
        Mission Status
      </div>

      {visible.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: "var(--text-muted)",
            padding: "8px 0",
            fontStyle: "italic",
          }}
        >
          No goals yet.
        </div>
      ) : (
        visible.map((g, idx) => (
          <div
            key={g.goalGuid}
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              paddingBlock: 7,
              borderBlockStart:
                idx === 0 ? "none" : "1px solid var(--border-default)",
            }}
          >
            <div
              style={{
                inlineSize: 10,
                blockSize: 10,
                borderRadius: "50%",
                background: assessmentToColor(g.latestAssessment),
                flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, fontSize: 13 }}>{g.nameKey}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
              {assessmentToLabel(g.latestAssessment)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

/** Map qualitative-ladder assessment to a palette color. Mirrors the
 *  MP schedule classifier banding so the dot color matches the rest of
 *  the page. Unknown / null → grey. */
function assessmentToColor(assessment: string | null | undefined): string {
  const p = getActiveIndicatorPalette();
  switch (assessment) {
    case "done":      return p.completed;
    case "ahead":
    case "on_track":  return p.green;
    case "behind":    return p.yellow;
    case "blocked":   return p.red;
    default:          return p.grey;
  }
}

function assessmentToLabel(assessment: string | null | undefined): string {
  switch (assessment) {
    case "done":     return "done";
    case "ahead":    return "ahead";
    case "on_track": return "on track";
    case "behind":   return "behind";
    case "blocked":  return "blocked";
    default:         return "no data";
  }
}
